// Settings service for managing trust thresholds and other configuration
import type { ExecutionMode } from '@agion/mission-stream'
import { api } from '@/shared/api/client'
import type { TrustTierKey } from '@/services/trust-tiers.service'

export interface CostSummary {
  organizationId: string
  monthlySpend: number
  yearToDateSpend: number
  todaySpend: number
  yesterdaySpend: number
}

export interface CostNotificationSettings {
  organizationId: string
  monthlyBudget: number    // USD
  hardLimit: boolean
  thresholds: number[]     // e.g. [70, 80, 90]
  configured: boolean      // true = persisted policy; false = server defaults (no alerts active)
  updatedAt?: string
  updatedBy?: string
  /** The server's verdict: 'budget_zero' when a saved budget of 0 under the hard limit stops new mission runs. */
  blockedReason?: 'budget_zero'
  /** Whether a saved 0 is kept here (no spend allowed) or stored as the default budget. */
  zeroBudgetAccepted?: boolean
}

export interface UsageHistoryOptions {
  limit: number
  modelOffset: number
  assistantOffset: number
  modelSearch: string
  assistantSearch: string
}

export interface UsageHistoryRow {
  modelId?: string
  providerType?: string
  /**
   * The model's name from the server's model registry, as the current month
   * names it (agion/Agion#2523). Absent when the registry has no name.
   */
  displayName?: string
  callerService?: string
  callerPurposes?: string[]
  inputTokens: number
  outputTokens: number
  cacheTokens: number
  cacheCreationTokens: number
  requestCount: number
  /** Null on a row whose every call went without reported usage (agion/Agion#2799). */
  totalCostUsd: number | null
  /** The row's calls without reported usage; absent when it has none. */
  usageUnknownCalls?: number
}

export interface UsageHistoryPage { total: number; limit: number; offset: number }

// Historical reporting has no recorded budget policy or component charges.
// Keep it separate from the legacy current-month settings contract.
export interface UsageHistory {
  month: string
  closed: true
  monthlySpend: number
  averageDailySpend: number
  budgetPolicyAvailable: false
  monthlyBudget: null
  thresholds: null
  hardLimit: null
  remainingBudget: null
  usagePercent: null
  dailySpend: BudgetMonitoringData['dailySpend']
  byModel: UsageHistoryRow[]
  byAssistant: UsageHistoryRow[]
  modelPage: UsageHistoryPage
  assistantPage: UsageHistoryPage
  dataCompleteness: { complete: false; reason: string }
  costCoverage: NonNullable<BudgetMonitoringData['costCoverage']>
  /**
   * The month's costliest missions the viewer may view, as the current month
   * reads them (agion/Agion#2523): [] when none spent anything, absent when
   * the read failed or the server is older.
   */
  topMissions?: BudgetMonitoringData['topMissions']
  /** As on BudgetMonitoringData. */
  tokenConventionEffectiveAt?: string
}

export interface BudgetMonitoringData {
  organizationId: string
  currency: string
  monthlyBudget: number
  monthlySpend: number
  remainingBudget: number
  usagePercent: number
  hardLimit: boolean
  thresholds: number[]
  nextThreshold?: number | null
  forecastMonthEndSpend?: number | null
  projectedExceedDate?: string | null
  averageDailySpend: number
  dailySpend: Array<{
    date: string
    spend: number
    cumulativeSpend: number
    missionCount?: number
  }>
  topMissions: Array<{
    missionId: string
    missionName: string
    spend: number
    percentageOfMonthlySpend: number
    status?: string
    // The mission's CURRENT execution strategy, served as execution_mode since
    // Agion#2025. Null when the mission is deleted, foreign or unknown: the
    // platform does not know its strategy, and the page must not invent one.
    executionMode?: ExecutionMode | null
    // The mission's recorded cache reads and writes, and its measured cached
    // share (cache reads over uncached input + cache reads + cache writes,
    // 0..1), served by governance; cachedShare is absent when the mission
    // recorded no input token (agion/Agion#2791). Never derived here.
    cacheTokens?: number
    cacheCreationTokens?: number
    cachedShare?: number
  }>
  byModel: BudgetModelUsage[]
  byAssistant?: BudgetAssistantUsage[]
  dataCompleteness: {
    complete: boolean
    hostsFailed: number
    hostsSkipped: number
    // Hosts that reported usage out of those expected to (gap cost-breakdown-fields).
    reporting?: number
    expected?: number
  }
  // Call-level cost coverage for the month: how many calls carry a cost and how
  // it splits between authoritative provider-reported charges and rate
  // estimates. Backend field: cost_coverage. Optional for older backends.
  costCoverage?: {
    totalCalls: number
    pricedCalls: number
    pricedPercent: number
    actualCostUsd: number
    estimatedCostUsd: number
    /**
     * Calls whose provider did not report usage, so their tokens and cost are
     * unknown (backend usage_unknown_calls, agion/Agion#2799). Absent on an
     * older server. Served by governance, never counted here.
     */
    usageUnknownCalls?: number
  }
  /**
   * When cached input started to be counted and priced once for every
   * provider (RFC 3339 UTC; backend token_convention_effective_at,
   * agion/Agion#2784). Absent when the platform does not state it; the page
   * then marks nothing and never derives a date of its own.
   */
  tokenConventionEffectiveAt?: string
  /**
   * False while the platform serves no cached share per agent
   * (cached_share_by_agent_available, agion/Agion#2791, #1865). Absent on an
   * older server.
   */
  cachedShareByAgentAvailable?: boolean
}

/** A cost part the server priced at the input rate because its own rate is missing (backend estimated_parts). */
export type EstimatedCostPart = 'cached_input' | 'cache_write'

/**
 * One provider's share of a model row, as the ledger recorded it (backend
 * provider_breakdown entry, ModelProviderUsage).
 */
export interface ModelProviderUsage {
  providerType: string
  inputTokens: number
  outputTokens: number
  cacheTokens: number
  cacheCreationTokens: number
  requestCount: number
  /** Null when every call of the group went without reported usage (agion/Agion#2799). */
  totalCostUsd: number | null
  usageUnknownCalls?: number
}

// Per-source spend breakdown for the current month, grouped by the service that
// made the LLM call. 'assistant' is the Agion Assistant; other values are
// missions, retrieval, governance, etc. Backend field: by_assistant /
// caller_service (auto-converted snake_case -> camelCase by the API client).
export interface BudgetAssistantUsage {
  callerService: string
  inputTokens: number
  outputTokens: number
  requestCount: number
  /** Null when every call of the caller went without reported usage (agion/Agion#2799). */
  totalCostUsd: number | null
  // Cache reads and writes and the priced parts, the same fields a model row
  // carries (agion/Agion#2786, challenge record A2). Optional for an older
  // server or a cached payload from before the fields.
  cacheTokens?: number
  cacheCreationTokens?: number
  inputCostUsd?: number
  cachedInputCostUsd?: number
  cacheWriteCostUsd?: number
  outputCostUsd?: number
  costSplitAvailable?: boolean
  estimatedParts?: EstimatedCostPart[]
  /** The caller's calls whose usage was not reported (backend usage_unknown_calls, agion/Agion#2799). */
  usageUnknownCalls?: number
}

export interface BudgetModelUsage {
  modelId: string
  // The ledger provider that served the calls; empty when several did, which
  // providerBreakdown then lists (agion/Agion#2764).
  providerType?: string
  providerBreakdown?: ModelProviderUsage[]
  // The provider behind an aggregator (OpenRouter), when the ledger knows it
  // (gap cost-breakdown-fields).
  upstreamProvider?: string
  displayName?: string
  primaryModality?: string
  defaultPurposes?: string[]
  inputTokens: number
  outputTokens: number
  cacheTokens: number
  requestCount: number
  inputCostUsd: number
  cachedInputCostUsd: number
  outputCostUsd: number
  // Null when every call of the model went without reported usage: the cost
  // is unknown, never $0 (backend usage_unknown_calls, agion/Agion#2799).
  totalCostUsd: number | null
  usageUnknownCalls?: number
  // True when the input/output cost split came from authoritative pricing
  // (model_registry or static metadata). When false, only totalCostUsd
  // is meaningful and the input/output columns should render '—'.
  costSplitAvailable: boolean
  // Cache writes and their cost part (agion/Agion#2786). Optional for an
  // older server or a cached payload from before the field.
  cacheCreationTokens?: number
  cacheWriteCostUsd?: number
  // The cache parts priced at the input rate because their own rate is
  // missing; absent when none is. A zero part not listed is a priced zero.
  estimatedParts?: EstimatedCostPart[]
  // Measured cached share (0..1) served by governance; absent when the model
  // recorded no input token (agion/Agion#2791). Never derived here.
  cachedShare?: number
}

export interface TrustThresholds {
  restrictedToLimited: number // Trust score percentage to exit restricted tier (0-100)
  limitedToStandard: number // Trust score percentage to reach standard tier (0-100)
  standardToElevated: number // Trust score percentage to reach elevated tier (0-100)
  verificationRateRestricted: number // Percentage of outputs to verify in restricted tier
  verificationRateLimited: number // Percentage of outputs to verify in limited tier
  verificationRateStandard: number // Percentage of outputs to verify in standard tier
  verificationRateElevated: number // Percentage of outputs to verify in elevated tier
}

export interface TokenBudgetSettings {
  defaultMonthlyLimit: number
  warningThreshold: number
  hardLimit: boolean
}

export interface SystemSettings {
  trustThresholds: TrustThresholds
  tokenBudget: TokenBudgetSettings
}

// Default settings with constraints
const DEFAULT_SETTINGS: SystemSettings = {
  trustThresholds: {
    restrictedToLimited: 30,
    limitedToStandard: 55,
    standardToElevated: 80,
    verificationRateRestricted: 100,
    verificationRateLimited: 50,
    verificationRateStandard: 10,
    verificationRateElevated: 0
  },
  tokenBudget: {
    defaultMonthlyLimit: 1000000,
    warningThreshold: 80,
    hardLimit: true
  }
}

// In-flight request deduplication + short TTL cache for fan-out endpoints.
// Prevents N components from triggering N parallel fan-out requests.
interface CacheEntry<T> {
  data: T
  expiresAt: number
}

interface CacheOptions {
  forceRefresh?: boolean
}

const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes
const FANOUT_REQUEST_OPTIONS = {
  timeout: 12000,
  retry: 0,
} as const

class SettingsService {
  private settings: SystemSettings = structuredClone(DEFAULT_SETTINGS)
  private storageKey = 'agion-system-settings'

  // Dedup: share in-flight promises so concurrent callers get the same result
  private inflight = new Map<string, Promise<unknown>>()
  // Cache: avoid re-fetching within TTL
  private cache = new Map<string, CacheEntry<unknown>>()

  constructor() {
    this.loadSettings()
  }

  // Load settings from localStorage (in production, this would be from backend)
  private loadSettings() {
    try {
      const stored = localStorage.getItem(this.storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        this.settings = this.validateSettings(parsed)
      }
    } catch {
      // Settings loading failed, using defaults
      this.settings = structuredClone(DEFAULT_SETTINGS)
    }
  }

  // Validate settings and apply constraints
  private validateSettings(settings: SystemSettings): SystemSettings {
    const validated = { ...settings }
    // Deep-clone nested object to avoid mutating the input
    validated.trustThresholds = { ...validated.trustThresholds }

    // Ensure thresholds are within valid range (0-100)
    const { trustThresholds } = validated
    trustThresholds.restrictedToLimited = Math.min(100, Math.max(0, trustThresholds.restrictedToLimited))
    trustThresholds.limitedToStandard = Math.min(100, Math.max(0, trustThresholds.limitedToStandard))
    trustThresholds.standardToElevated = Math.min(100, Math.max(0, trustThresholds.standardToElevated))

    // Apply constraint: thresholds must be in ascending order
    if (trustThresholds.limitedToStandard < trustThresholds.restrictedToLimited) {
      trustThresholds.limitedToStandard = trustThresholds.restrictedToLimited
    }
    if (trustThresholds.standardToElevated < trustThresholds.limitedToStandard) {
      trustThresholds.standardToElevated = trustThresholds.limitedToStandard
    }

    // Validate verification rates
    trustThresholds.verificationRateRestricted = Math.min(100, Math.max(0, trustThresholds.verificationRateRestricted))
    trustThresholds.verificationRateLimited = Math.min(100, Math.max(0, trustThresholds.verificationRateLimited))
    trustThresholds.verificationRateStandard = Math.min(100, Math.max(0, trustThresholds.verificationRateStandard))
    trustThresholds.verificationRateElevated = Math.min(100, Math.max(0, trustThresholds.verificationRateElevated))

    return validated
  }

  // Get current settings
  getSettings(): SystemSettings {
    return { ...this.settings }
  }

  // Get trust thresholds
  getTrustThresholds(): TrustThresholds {
    return { ...this.settings.trustThresholds }
  }

  // Update trust thresholds with validation
  updateTrustThresholds(thresholds: Partial<TrustThresholds>): TrustThresholds {
    const newThresholds = {
      ...this.settings.trustThresholds,
      ...thresholds
    }

    // Apply constraints
    newThresholds.restrictedToLimited = Math.min(100, Math.max(0, newThresholds.restrictedToLimited))
    newThresholds.limitedToStandard = Math.min(100, Math.max(0, newThresholds.limitedToStandard))
    newThresholds.standardToElevated = Math.min(100, Math.max(0, newThresholds.standardToElevated))

    // Ensure thresholds are in ascending order
    if (newThresholds.limitedToStandard < newThresholds.restrictedToLimited) {
      throw new Error('Standard threshold cannot be lower than limited threshold')
    }
    if (newThresholds.standardToElevated < newThresholds.limitedToStandard) {
      throw new Error('Elevated threshold cannot be lower than standard threshold')
    }

    // Validate verification rates
    newThresholds.verificationRateRestricted = Math.min(100, Math.max(0, newThresholds.verificationRateRestricted))
    newThresholds.verificationRateLimited = Math.min(100, Math.max(0, newThresholds.verificationRateLimited))
    newThresholds.verificationRateStandard = Math.min(100, Math.max(0, newThresholds.verificationRateStandard))
    newThresholds.verificationRateElevated = Math.min(100, Math.max(0, newThresholds.verificationRateElevated))

    this.settings.trustThresholds = newThresholds
    this.saveSettings()

    // In production, this would also call backend API
    // await api.updateTrustThresholds(newThresholds)

    return newThresholds
  }

  // Update token budget settings
  updateTokenBudget(budget: Partial<TokenBudgetSettings>): TokenBudgetSettings {
    this.settings.tokenBudget = {
      ...this.settings.tokenBudget,
      ...budget
    }
    this.saveSettings()
    return this.settings.tokenBudget
  }

  // Save settings to localStorage (in production, this would be to backend)
  private saveSettings() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.settings))
    } catch {
      // Settings save failed - using in-memory state
    }
  }

  // Reset to default settings
  resetToDefaults() {
    this.settings = structuredClone(DEFAULT_SETTINGS)
    this.saveSettings()
    return this.settings
  }

  // Get trust stage based on score and current thresholds
  getTrustStage(trustScore: number): TrustTierKey {
    const scorePercent = trustScore // Already 0-100 scale
    const { restrictedToLimited, limitedToStandard, standardToElevated } = this.settings.trustThresholds

    if (scorePercent < restrictedToLimited) {
      return 'restricted'
    } else if (scorePercent < limitedToStandard) {
      return 'limited'
    } else if (scorePercent < standardToElevated) {
      return 'standard'
    } else {
      return 'elevated'
    }
  }

  // Get verification rate for a trust stage
  getVerificationRate(stage: TrustTierKey): number {
    switch (stage) {
      case 'restricted':
        return this.settings.trustThresholds.verificationRateRestricted
      case 'limited':
        return this.settings.trustThresholds.verificationRateLimited
      case 'standard':
        return this.settings.trustThresholds.verificationRateStandard
      case 'elevated':
        return this.settings.trustThresholds.verificationRateElevated
      default:
        return 100
    }
  }

  // Deduplicate concurrent requests and cache results for TTL.
  // Multiple components calling the same endpoint simultaneously share one request.
  private async cachedGet<T>(key: string, fetcher: () => Promise<T>, options: CacheOptions = {}): Promise<T> {
    // Return cached data if still fresh
    const cached = this.cache.get(key) as CacheEntry<T> | undefined
    if (!options.forceRefresh && cached && Date.now() < cached.expiresAt) {
      return cached.data
    }

    // Deduplicate: if a request is already in-flight, wait for it
    const existing = this.inflight.get(key) as Promise<T> | undefined
    if (existing) return existing

    const promise = fetcher().then(
      (data) => {
        this.cache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS })
        this.inflight.delete(key)
        return data
      },
      (err) => {
        this.inflight.delete(key)
        throw err
      },
    )
    this.inflight.set(key, promise)
    return promise
  }

  // Get cost summary (cached + deduplicated — fans out to BYOH hosts)
  async getCostSummary(): Promise<CostSummary> {
    return this.cachedGet('cost-summary', () =>
      api.get<CostSummary>('governance/usage/summary', undefined, FANOUT_REQUEST_OPTIONS),
    )
  }

  // Get shared cost notification settings from backend (fast, but still deduplicated)
  async getCostNotificationSettings(): Promise<CostNotificationSettings> {
    return this.cachedGet('cost-notifications', () =>
      api.get<CostNotificationSettings>('governance/cost-notifications'),
    )
  }

  // Update shared cost notification settings (owner/admin only)
  async updateCostNotificationSettings(
    settings: Partial<Pick<CostNotificationSettings, 'monthlyBudget' | 'hardLimit' | 'thresholds'>>
  ): Promise<CostNotificationSettings> {
    const result = await api.put<CostNotificationSettings>('governance/cost-notifications', settings)
    // Invalidate caches so next read picks up the new values
    this.cache.delete('cost-notifications')
    this.cache.delete('cost-summary')
    this.cache.delete('budget-monitoring')
    return result
  }

  // Get budget monitoring data (cached + deduplicated — fans out to BYOH hosts)
  async getUsageMonths(): Promise<string[]> {
    const result = await api.get<{ months: string[] }>('governance/usage/months')
    return result.months
  }

  async getUsageHistory(month: string, options: UsageHistoryOptions): Promise<UsageHistory> {
    const result = await api.get<UsageHistory>('governance/usage/monitoring', {
      month, limit: options.limit,
      model_offset: options.modelOffset, assistant_offset: options.assistantOffset,
      model_search: options.modelSearch, assistant_search: options.assistantSearch,
    }, FANOUT_REQUEST_OPTIONS)
    // Older servers ignore month and return current data. Never relabel that
    // response as history, or reuse current policy for a closed month.
    if (result.month !== month || result.closed !== true || !result.modelPage || !result.assistantPage) {
      throw new Error('Usage history response does not support the requested month')
    }
    return result
  }

  async getBudgetMonitoring(options: CacheOptions = {}): Promise<BudgetMonitoringData> {
    return this.cachedGet('budget-monitoring', () =>
      api.get<BudgetMonitoringData>('governance/usage/monitoring', undefined, FANOUT_REQUEST_OPTIONS),
      options,
    )
  }
}

// Export singleton instance
export const settingsService = new SettingsService()
